/**
 * Intro Request effects - side effect handlers.
 * 
 * Elm-ish architecture: Side effects are isolated here.
 * The effect runner (useIntroRequestProgram.ts) calls these functions.
 * 
 * Security posture:
 * - All API calls use identity-minimized endpoints
 * - No credentials logged
 * - Safe error messages only
 * - No sensitive data exposed in error states
 * - Only sponsors may submit intro requests (server-side enforced)
 * - Fingerprint collection for audit context (fail-open)
 * 
 * TODO(ACT 7): Emit intro_request_started audit event on page load
 * TODO(ACT 7): Emit intro_request_submitted audit event on submit success
 * TODO(ACT 7): Emit duplicate_intro_request_attempted audit event
 */
import { checkIntroRequestExists, submitIntroRequest, type IntroRequestDraft } from '@/api/introRequests';
import { getProfileById, ProfileError } from '@/api/profiles';
import { useAuthStore } from '@/store/authStore';
import { collectFingerprint } from '@/utils/fingerprint';
import type { IntroRequestEffect } from './update';
import type { IntroRequestCard } from './model';
import { toIntroRequestCard } from './model';

/**
 * Effect handler context - injected by the effect runner.
 */
export interface EffectContext {
  dispatch: (msg: import('./messages').IntroRequestMsg) => void;
}

/**
 * Run a single effect.
 * Returns a cleanup function if needed.
 */
export async function runEffect(
  effect: IntroRequestEffect,
  context: EffectContext
): Promise<(() => void) | void> {
  switch (effect.type) {
    case 'LoadCard':
      return runLoadCard(context, effect.cardId);
      
    case 'CheckDuplicate':
      return runCheckDuplicate(context, effect.cardId);
      
    case 'SubmitRequest':
      return runSubmitRequest(context, effect.cardId, effect.message);
      
    default:
      const _exhaustive: never = effect;
      return;
  }
}

/**
 * Load card data by ID from the real API.
 * Replaces seeded data with actual backend profile lookup.
 */
async function runLoadCard(context: EffectContext, cardId: string): Promise<void> {
  try {
    const registrationKind = useAuthStore.getState().registrationKind;
    const profile = await getProfileById(cardId, registrationKind);
    const introCard: IntroRequestCard = toIntroRequestCard(profile);
    context.dispatch({
      type: 'CardLoaded',
      card: introCard,
    });
  } catch (err) {
    if (err instanceof ProfileError && err.safe) {
      // 403 means this page is not available for this account type
      if (err.statusCode === 403) {
        context.dispatch({
          type: 'CardLoadFailed',
          error: 'This page is not available for your account type',
        });
        return;
      }
      // 404 - profile not found
      context.dispatch({
        type: 'CardLoadFailed',
        error: 'Card not found',
      });
    } else {
      // Server error or network failure
      context.dispatch({
        type: 'CardLoadFailed',
        error: 'Failed to load card',
      });
    }
  }
}

/**
 * Check if intro request already exists for this card.
 * Uses session storage as local placeholder.
 * 
 * TODO(ACT 7): Replace with: GET /api/intro-requests?cardId=<cardId>
 * TODO(ACT 7): Emit duplicate_intro_request_attempted audit event if exists
 */
async function runCheckDuplicate(context: EffectContext, cardId: string): Promise<void> {
  try {
    const existing = await checkIntroRequestExists(cardId);
    
    if (existing) {
      context.dispatch({
        type: 'DuplicateDetected',
        requestId: existing.requestId,
      });
    } else {
      context.dispatch({
        type: 'NoDuplicate',
      });
    }
  } catch (err) {
    // On duplicate check failure, continue to form
    // (duplicate check is best-effort, don't block the flow)
    context.dispatch({
      type: 'NoDuplicate',
    });
  }
}

/**
 * Submit the intro request.
 * Only sponsors may submit intro requests (server-side enforced).
 * Friends receive 403 and see safe error.
 * Fingerprint is collected for fraud-resistant audit context.
 * Collection failure is non-blocking - submission proceeds without fingerprint.
 * 
 * TODO(ACT 7): Emit intro_request_started audit event (already done on page load)
 * TODO(ACT 7): Emit intro_request_submitted audit event on success
 */
async function runSubmitRequest(
  context: EffectContext,
  cardId: string,
  message: string
): Promise<void> {
  // TODO(ACT 7): Emit intro_request_started audit event (already done on page load)
  
  // Check if user is a sponsor before attempting submit
  const registrationKind = useAuthStore.getState().registrationKind;
  if (registrationKind === 'friend') {
    // Friends cannot submit intro requests - show safe error
    context.dispatch({
      type: 'SubmitFailed',
      error: 'This feature is not available for your account type',
    });
    return;
  }
  
  try {
    // Collect fingerprint for fraud-resistant audit context.
    // This is optional - failure to collect does not block submission.
    // Backend handles missing fingerprint with degraded audit context.
    // Covers: BR-FRAUD-001, REQ-FRAUD-FINGERPRINT-CAPTURE-001, REQ-FRAUD-AUDIT-CONTEXT-001
    let fingerprint;
    try {
      fingerprint = await collectFingerprint();
    } catch {
      // Fingerprint collection failed - proceed without it (fail-open)
      fingerprint = undefined;
    }
    
    const draft: IntroRequestDraft = {
      cardId,
      message: message.trim() || undefined,
      acknowledged: true,
      fingerprint,
    };
    
    const response = await submitIntroRequest(draft);
    
    context.dispatch({
      type: 'SubmitSucceeded',
      requestId: response.requestId,
    });
  } catch (err) {
    // Check if it's a 403 from the API
    if (err instanceof Error && err.message.includes('403')) {
      context.dispatch({
        type: 'SubmitFailed',
        error: 'This feature is not available for your account type',
      });
      return;
    }
    context.dispatch({
      type: 'SubmitFailed',
      error: 'Failed to submit request. Please try again.',
    });
  }
}
